/**
 * Shared editor context — single source of truth for the v3 layout editor.
 * Holds the current page layout tree + selection state + tree-mutation
 * primitives that node editors call.
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type {
  CarouselNode,
  ColumnNode,
  ContainerChild,
  ContainerNode,
  ContainerVariant,
  LabelNode,
  LabelPreset,
  LayoutNode,
  PageLayout,
  RowNode,
  SlotNode,
  SlotPreset,
} from '@news-cms/shared';

function newId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID().replace(/-/g, '').slice(0, 6)}`;
}

const DEFAULT_PRESET: SlotPreset = 'card';

export const EMPTY_ROW: Omit<RowNode, 'id'> = { type: 'row', gap: 3, children: [] };

export function makeContainer(variant: ContainerVariant = 'fluid'): ContainerNode {
  return { id: newId('cont'), type: 'container', variant, children: [] };
}
export function makeRow(): RowNode {
  return { id: newId('row'), type: 'row', gap: 1, children: [makeColumn(12)] };
}
export function makeColumn(span: number): ColumnNode {
  return {
    id: newId('col'),
    type: 'column',
    span: Math.max(1, Math.min(12, Math.round(span))),
    children: [],
  };
}
export function makeSlot(groupSlug = '', preset: SlotPreset = DEFAULT_PRESET): SlotNode {
  return { id: newId('slot'), type: 'slot', groupSlug, itemIndex: 0, preset };
}
export function makeCarousel(groupSlug = '', preset: SlotPreset = DEFAULT_PRESET): CarouselNode {
  return { id: newId('car'), type: 'carousel', groupSlug, preset };
}
const DEFAULT_LABEL_PRESET: LabelPreset = 'h2';
export function makeLabel(
  text = 'หัวข้อใหม่',
  preset: LabelPreset = DEFAULT_LABEL_PRESET,
): LabelNode {
  return { id: newId('label'), type: 'label', text, preset };
}

/* ---------- Tree mutation primitives ---------- */

/** Recursively map nodes. Visitor returns the new node (or `null` to remove). */
function mapTree(
  node: LayoutNode,
  visitor: (n: LayoutNode) => LayoutNode | null,
): LayoutNode | null {
  const visited = visitor(node);
  if (visited === null) return null;
  // Leaves (no children to walk)
  if (visited.type === 'slot' || visited.type === 'carousel' || visited.type === 'label')
    return visited;
  if (visited.type === 'row') {
    const children = visited.children
      .map((c) => mapTree(c, visitor))
      .filter((c): c is ColumnNode => c !== null && c.type === 'column');
    return { ...visited, children };
  }
  // container or column — both accept the same child union (no Column inside)
  const children = visited.children
    .map((c) => mapTree(c, visitor))
    .filter((c): c is ContainerChild => c !== null && c.type !== 'column');
  return { ...visited, children };
}

function updateById(
  layout: PageLayout,
  id: string,
  updater: (n: LayoutNode) => LayoutNode,
): PageLayout {
  const root = mapTree(layout.root, (n) => (n.id === id ? updater(n) : n));
  if (!root || root.type !== 'container') return layout;
  return { ...layout, root };
}

function deleteById(layout: PageLayout, id: string): PageLayout {
  if (id === layout.root.id) return layout; // root undeletable
  const root = mapTree(layout.root, (n) => (n.id === id ? null : n));
  if (!root || root.type !== 'container') return layout;
  return { ...layout, root };
}

function addChild(layout: PageLayout, parentId: string, newChild: LayoutNode): PageLayout {
  return updateById(layout, parentId, (parent) => {
    if (parent.type === 'slot' || parent.type === 'carousel' || parent.type === 'label')
      return parent; // leaves
    if (parent.type === 'row') {
      if (newChild.type !== 'column') return parent; // rows hold only columns
      return { ...parent, children: [...parent.children, newChild] };
    }
    if (newChild.type === 'column') return parent; // columns only via rows
    return { ...parent, children: [...parent.children, newChild] };
  });
}

/** Swap a child with its previous/next sibling inside the parent's children
 *  array. Walks the tree looking for any parent whose children contain the
 *  target id; reorders in place. Returns a fresh layout. */
function moveById(layout: PageLayout, id: string, direction: 'up' | 'down'): PageLayout {
  const swap = <T,>(arr: T[], i: number, j: number): T[] => {
    const next = [...arr];
    [next[i], next[j]] = [next[j]!, next[i]!];
    return next;
  };

  const reorderChildren = <T extends { id: string }>(children: T[]): T[] | null => {
    const idx = children.findIndex((c) => c.id === id);
    if (idx === -1) return null;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= children.length) return null; // edge — no-op
    return swap(children, idx, targetIdx);
  };

  const walk = (node: LayoutNode): LayoutNode => {
    if (node.type === 'slot' || node.type === 'carousel' || node.type === 'label') return node;
    if (node.type === 'row') {
      const reordered = reorderChildren(node.children);
      if (reordered) return { ...node, children: reordered };
      return { ...node, children: node.children.map((c) => walk(c) as ColumnNode) };
    }
    // container or column — both accept (Container | Row | Slot | Carousel)
    const reordered = reorderChildren(node.children);
    if (reordered) return { ...node, children: reordered };
    return { ...node, children: node.children.map((c) => walk(c) as ContainerChild) };
  };

  const root = walk(layout.root);
  if (root.type !== 'container') return layout;
  return { ...layout, root };
}

/* ---------- Context ---------- */

interface EditorContextValue {
  layout: PageLayout;
  selectedId: string | null;
  select: (id: string | null) => void;

  // Mutations
  updateContainer: (id: string, patch: Partial<Pick<ContainerNode, 'variant'>>) => void;
  updateRow: (id: string, patch: Partial<Pick<RowNode, 'gap'>>) => void;
  updateColumn: (id: string, patch: Partial<Pick<ColumnNode, 'span'>>) => void;
  updateSlot: (
    id: string,
    patch: Partial<Pick<SlotNode, 'groupSlug' | 'preset' | 'itemIndex'>>,
  ) => void;
  updateCarousel: (id: string, patch: Partial<Pick<CarouselNode, 'groupSlug' | 'preset'>>) => void;
  updateLabel: (id: string, patch: Partial<Pick<LabelNode, 'text' | 'url' | 'preset'>>) => void;

  addContainerToParent: (parentId: string, variant?: ContainerVariant) => void;
  addRowToParent: (parentId: string) => void;
  addColumnToRow: (rowId: string, span?: number) => void;
  addSlotToParent: (parentId: string) => void;
  addCarouselToParent: (parentId: string) => void;
  addLabelToParent: (parentId: string) => void;

  /** Swap a node with its previous/next sibling within its parent. No-op
   *  when at edge (no sibling on that side). Works for any node type. */
  move: (id: string, direction: 'up' | 'down') => void;
  remove: (id: string) => void;
}

const EditorContext = createContext<EditorContextValue | null>(null);

export function EditorProvider({
  layout,
  onChange,
  children,
}: {
  layout: PageLayout;
  onChange: (next: PageLayout) => void;
  children: React.ReactNode;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const updateContainer = useCallback(
    (id: string, patch: Partial<Pick<ContainerNode, 'variant'>>) => {
      onChange(updateById(layout, id, (n) => (n.type === 'container' ? { ...n, ...patch } : n)));
    },
    [layout, onChange],
  );
  const updateRow = useCallback(
    (id: string, patch: Partial<Pick<RowNode, 'gap'>>) => {
      onChange(
        updateById(layout, id, (n) => (n.type === 'row' ? { ...n, gap: patch.gap ?? n.gap } : n)),
      );
    },
    [layout, onChange],
  );
  const updateColumn = useCallback(
    (id: string, patch: Partial<Pick<ColumnNode, 'span'>>) => {
      onChange(
        updateById(layout, id, (n) =>
          n.type === 'column'
            ? { ...n, span: Math.max(1, Math.min(12, Math.round(patch.span ?? n.span))) }
            : n,
        ),
      );
    },
    [layout, onChange],
  );
  const updateSlot = useCallback(
    (id: string, patch: Partial<Pick<SlotNode, 'groupSlug' | 'preset' | 'itemIndex'>>) => {
      onChange(updateById(layout, id, (n) => (n.type === 'slot' ? { ...n, ...patch } : n)));
    },
    [layout, onChange],
  );
  const updateCarousel = useCallback(
    (id: string, patch: Partial<Pick<CarouselNode, 'groupSlug' | 'preset'>>) => {
      onChange(updateById(layout, id, (n) => (n.type === 'carousel' ? { ...n, ...patch } : n)));
    },
    [layout, onChange],
  );
  const updateLabel = useCallback(
    (id: string, patch: Partial<Pick<LabelNode, 'text' | 'url' | 'preset'>>) => {
      onChange(
        updateById(layout, id, (n) => {
          if (n.type !== 'label') return n;
          // Empty url collapses to undefined to keep the JSON clean.
          const next: LabelNode = { ...n, ...patch };
          if ('url' in patch && (!patch.url || patch.url.trim() === '')) {
            delete next.url;
          }
          return next;
        }),
      );
    },
    [layout, onChange],
  );

  const addContainerToParent = useCallback(
    (parentId: string, variant: ContainerVariant = 'fluid') => {
      onChange(addChild(layout, parentId, makeContainer(variant)));
    },
    [layout, onChange],
  );
  const addRowToParent = useCallback(
    (parentId: string) => {
      onChange(addChild(layout, parentId, makeRow()));
    },
    [layout, onChange],
  );
  const addColumnToRow = useCallback(
    (rowId: string, span = 6) => {
      onChange(addChild(layout, rowId, makeColumn(span)));
    },
    [layout, onChange],
  );
  const addSlotToParent = useCallback(
    (parentId: string) => {
      onChange(addChild(layout, parentId, makeSlot()));
    },
    [layout, onChange],
  );
  const addCarouselToParent = useCallback(
    (parentId: string) => {
      onChange(addChild(layout, parentId, makeCarousel()));
    },
    [layout, onChange],
  );
  const addLabelToParent = useCallback(
    (parentId: string) => {
      onChange(addChild(layout, parentId, makeLabel()));
    },
    [layout, onChange],
  );

  const move = useCallback(
    (id: string, direction: 'up' | 'down') => {
      onChange(moveById(layout, id, direction));
    },
    [layout, onChange],
  );
  const remove = useCallback(
    (id: string) => {
      onChange(deleteById(layout, id));
      setSelectedId((prev) => (prev === id ? null : prev));
    },
    [layout, onChange],
  );

  const value = useMemo<EditorContextValue>(
    () => ({
      layout,
      selectedId,
      select: setSelectedId,
      updateContainer,
      updateRow,
      updateColumn,
      updateSlot,
      updateCarousel,
      updateLabel,
      addContainerToParent,
      addRowToParent,
      addColumnToRow,
      addSlotToParent,
      addCarouselToParent,
      addLabelToParent,
      move,
      remove,
    }),
    [
      layout,
      selectedId,
      updateContainer,
      updateRow,
      updateColumn,
      updateSlot,
      updateCarousel,
      updateLabel,
      addContainerToParent,
      addRowToParent,
      addColumnToRow,
      addSlotToParent,
      addCarouselToParent,
      addLabelToParent,
      move,
      remove,
    ],
  );

  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
}

export function useEditor(): EditorContextValue {
  const ctx = useContext(EditorContext);
  if (!ctx) throw new Error('useEditor must be used inside <EditorProvider>');
  return ctx;
}
